import React, { useContext } from 'react';

import { ZoomOutIcon, ZoomInIcon } from '@vlabs/icons';
import { Modal, Slider, Control } from '@vlabs/uikit';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  QueueContext,
  QUEUE_CARD_MODES,
  DISPLAY_ZONES_OPTIONS,
  DOTS_COLORS_OPTIONS,
} from '../queue-context/QueueContext';
import st from './CardSettingsModal.module.sass';

export function CardSettingsModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { queueCardSettings, setQueueCardSettings } = useContext(QueueContext);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      mode: queueCardSettings.mode,
      displayZones: new Set(queueCardSettings.displayZones),
      showDots: queueCardSettings.showDotsOnFaces.enabled,
      dotSize: queueCardSettings.showDotsOnFaces.size,
      dotColor: queueCardSettings.showDotsOnFaces.color,
    },
  });

  const onSubmit = (data) => {
    setQueueCardSettings({
      mode: data.mode,
      displayZones: Array.from(data.displayZones),
      showDotsOnFaces: {
        enabled: data.showDots,
        size: data.dotSize,
        color: data.dotColor,
      },
    });
    onClose();
  };

  const toggleZone = (zone) => {
    const current = getValues('displayZones');
    const newZones = new Set(current);
    if (newZones.has(zone)) {
      newZones.delete(zone);
    } else {
      newZones.add(zone);
    }
    setValue('displayZones', newZones);
  };

  const isRetranslationOn = watch('mode') === QUEUE_CARD_MODES.RETRANSLATION;

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} size="large" data-testid="cardSettingsModal">
      <form className={st.Content} onSubmit={handleSubmit(onSubmit)}>
        <div className={st.Title}>{t('queue:card settings modal.title')}</div>
        <div className={st.Divider} />

        <div className={st.Subtitle}>{t('queue:card settings modal.mode subtitle')}</div>
        <Controller
          control={control}
          name="mode"
          data-testid="cardPreviewMode"
          render={({ field: { value, onChange } }) => (
            <>
              <Control.Radio
                checked={value === QUEUE_CARD_MODES.LAST_FRAME}
                label={t('queue:card settings modal.last frame radio button.label')}
                name="mode"
                data-testid="cardPreviewRadioLastFrame"
                onChange={onChange}
                value={QUEUE_CARD_MODES.LAST_FRAME}
              />
              <Control.Radio
                checked={value === QUEUE_CARD_MODES.RETRANSLATION}
                label={t('queue:card settings modal.retranslation radio button.label')}
                name="mode"
                data-testid="cardPreviewRadioTranslation"
                onChange={onChange}
                value={QUEUE_CARD_MODES.RETRANSLATION}
              />
            </>
          )}
        />

        <div className={st.Divider} />
        <div className={st.Subtitle}>{t('queue:card settings modal.zones subtitle')}</div>

        <Controller
          control={control}
          name="displayZones"
          render={() => (
            <>
              <Control.Checkbox
                checked={watch('displayZones').has(DISPLAY_ZONES_OPTIONS.ROI)}
                disabled={isRetranslationOn}
                label={t('queue:card settings modal.roi checkbox.label')}
                name={`displayZone:${DISPLAY_ZONES_OPTIONS.ROI}`}
                data-testid="cardSettingsCheckboxROI"
                onChange={() => toggleZone(DISPLAY_ZONES_OPTIONS.ROI)}
              />
              <Control.Checkbox
                checked={watch('displayZones').has(DISPLAY_ZONES_OPTIONS.DROI)}
                disabled={isRetranslationOn}
                label={t('queue:card settings modal.droi checkbox.label')}
                name={`displayZone:${DISPLAY_ZONES_OPTIONS.DROI}`}
                data-testid="cardSettingsCheckboxDROI"
                onChange={() => toggleZone(DISPLAY_ZONES_OPTIONS.DROI)}
              />
            </>
          )}
        />

        <div className={st.Divider} />
        <div className={st.Subtitle}>{t('queue:card settings modal.dots subtitle')}</div>

        <Controller
          control={control}
          name="showDots"
          render={({ field: { value, onChange } }) => (
            <Control.Checkbox
              checked={value}
              disabled={isRetranslationOn}
              label={t('queue:card settings modal.enable dots checkbox.label')}
              name="showDots"
              data-testid="cardSettingsCheckboxShowDots"
              onChange={onChange}
            />
          )}
        />

        <div className={st.SizeSliderWrapper}>
          <span>{t('queue:card settings modal.dots size slider.subtitle')}</span>
          <div className={st.SliderWithIcons}>
            <ZoomOutIcon />
            <Controller
              control={control}
              name="dotSize"
              data-testid="cardSettingsSliderDotSize"
              render={({ field: { value, onChange } }) => (
                <Slider
                  className={st.Slider}
                  defaultValue={value}
                  disabled={isRetranslationOn}
                  max={30}
                  min={5}
                  onChange={onChange}
                />
              )}
            />
            <ZoomInIcon />
          </div>
        </div>

        <div className={st.ColorSelector}>
          <span>{t('queue:card settings modal.color selector.subtitle')}</span>
          <div className={st.ColorCheckboxes}>
            {[
              DOTS_COLORS_OPTIONS.BLACK,
              DOTS_COLORS_OPTIONS.POSITIVE,
              DOTS_COLORS_OPTIONS.NEGATIVE,
              DOTS_COLORS_OPTIONS.PRIMARY,
            ].map((color) => (
              <Controller
                control={control}
                key={color}
                name="dotColor"
                render={({ field: { value, onChange } }) => (
                  <Control.Checkbox
                    checked={value === color}
                    className={st[color]}
                    disabled={isRetranslationOn}
                    name="dotColor"
                    onChange={() => onChange(color)}
                    value={color}
                  />
                )}
              />
            ))}
          </div>
        </div>

        <div className={st.Divider} />
        <div className={st.ButtonSection}>
          <Control.Button
            data-testid="CardSettingsModal_submitButton"
            type="submit"
          >
            {t('queue:card settings modal.submit button')}
          </Control.Button>
          <Control.Button
            data-testid="CardSettingsModal_cancelButton"
            kind="secondary"
            onClick={onClose}
            type="button"
          >
            {t('queue:card settings modal.cancel button')}
          </Control.Button>
        </div>
      </form>
    </Modal>
  );
}
